import getEditDistance from 'js-levenshtein'
import values from 'mout/object/values'

/**
 * Generates warning and error messages regarding student data in
 * a csv data upload.
 * @param {array} uploadData The parsed array of row objects from the csv upload
 * @param {object} records All of the db records we got from initVerification (e.g. students, educators, classes, groupUAs, and userAssessments)
 * @param {string} schoolYear The active school year ('2018' for 2018-19, for example)
 * @param {object} assessment The assessment we are uploading data about
 * @returns {object} { studentWarnings, studentErrors }
 */
export default function verifyStudents(uploadData, records, schoolYear, assessment) {
  const studentWarnings = []
  const studentErrors = []
  const studentRecords = records.students
  const { userAssessments } = records
  
  // Check each row of the upload
  uploadData.forEach(student => {
    // If a row does indicate a student email, check name against near misses.  
    // Provide suggested email/username if there is a potential match. (Warning)
    if (student.email) {
      const { email } = student
      // To save computation, only check names against one another if
      // their first letters are the same.
      const firstLetterMatches = values(studentRecords).filter(st => {
        return (
          st.email && 
          email && 
          st.email.toLowerCase()[0] === email.toLowerCase()[0]
        )
      })
      // The first time a similar email is found compared to this
      // student's, flag it (but don't flag multiple similarities)
      // for one email
      firstLetterMatches.some(match => {
        if (getEditDistance(match.email.toLowerCase(), email.toLowerCase()) < 3) {
          const { First, Last } = student
          const warning = {
            warning: `Student ${First} ${Last}'s email address of ${email} is similar to 
                      an email address on record: ${match.email} belonging to ${match.fullName}
                      and may be the same person.`,
            rows: [student.row],
          }
          studentWarnings.push(warning)
          return true
        } else return false
      })
    } else if (student.username) {
      // If a row does indicate a student username, check username against near misses.  
      // Provide suggested email/username if there is a potential match. (Warning)
      const { username } = student
      // Only run this check against other usernames that have the same
      // first letter to make cheaper. 
      const firstLetterMatches = values(studentRecords).filter(st => {
        return (
          st.username && 
          username && 
          st.username.toLowerCase()[0] === username.toLowerCase()[0]
        )
      })
      // Flag the first instance of a similar username, if there is one. Don't
      // flag more than one instance per student.
      firstLetterMatches.some(match => {
        if (getEditDistance(match.username.toLowerCase(), username.toLowerCase()) < 3) {
          const { First, Last } = student
          const warning = {
            warning: `Student ${First} ${Last}'s username of ${username} is similar to 
                      a username on record: ${match.username} belonging to ${match.fullName}
                      and may be the same person.`,
            rows: [student.row],
          }
          studentWarnings.push(warning)
          return true
        } else return false
      })
    } else if (student.Last && student.First && student.Last.length > 1) {
      // If a row does not indicate a student username or email, 
      // then check first and last name against existing data. Provide  
      // suggested email/username if there is a potential match. (Warning)
      // Potential match is either exact match on last name or 
      // close edit distance and exact match on first letter of first name
      
      // only consider other records with last names with same first letter
      // to make this process cheaper.
      const matches = values(studentRecords).filter(st => {
        return st.lastName.toLowerCase()[0] === student.Last.toLowerCase()[0]
      })
      const studentLastLC = student.Last.toLowerCase()
      
      // if the edit distance between the student last name and
      // a student in the students collection (studentRecords) is
      // small, and if their first names start with the same initial,
      // flag it.
      matches.some(match => {
        const matchLastLC = match.lastName.toLowerCase()
        // we should only look at the same number of letters in the student record (match.lastName)
        // as the number of letters in the uploadCSV row (student.Last) since the
        // student name in the CSV may be abbreviated.
        const matchSub = matchLastLC.substring(0, studentLastLC.length)
        if (studentLastLC === matchSub || getEditDistance(studentLastLC, matchSub) < 3) {
          if (match.firstName.toLowerCase()[0] === student.First.toLowerCase()[0]) {
            const { fullName, email, username } = match
            const warning = {
              warning: `Student ${student.First} ${student.Last} has a name similar to
                        a student on record: ${fullName}. The student on record's 
                        ${email ? 'email' : 'username'} is ${email || username}.`,
              rows: [student.row],
            }
            studentWarnings.push(warning)
            return true
          }
        }
      })
    } else if (student.Last && student.Last.length < 2) {
      // if the upload last name exists but is very short, flag it
      const warning = {
        warning: `Student ${student.First} ${student.Last} has a very
                  short last name.`,
        rows: [student.row],
      }
      studentWarnings.push(warning)
    }
  })

  // If a cell has a student username or email, 
  // check that this assessment and year hasn't already been uploaded
  // Still need to fill in line 114 for same if there is 
  // a student email already existing
  uploadData.forEach(student => {
    if (student.username) {
      // matching UAs are ones that have the same school year and assesmentId
      const matchingUAs = values(userAssessments).filter(uA => {
        return uA.assessmentSetId === assessment.id && uA.year === `sy${schoolYear}`
      })
      // get the student's info per the collection
      const recordStudent = values(studentRecords).filter(sR => sR.username === student.username)[0]

      // if the student isn't in the collection, stop this check for this iteration
      if (!recordStudent) return

      // of the matching UAs, find any that belong to the student
      const matchingUAsWithSameStudent = matchingUAs.filter(mUA => mUA.userId === recordStudent.id)

      // if there is a userAssessment on record for this student with same year
      // and assessmentId, flag it
      if (matchingUAsWithSameStudent.length > 0) {
        const warning = {
          warning: `Student ${recordStudent.fullName} already has a ${assessment.displayName} 
                    recorded for the ${schoolYear} school year. Are you sure you want to overwrite?`,
          rows: [student.row],
        }
        studentWarnings.push(warning)
      }
    } else if (student.email) {
      // handle checking for a duplicate UA if we have the student's email

      // matchingUAs are ones with same school year and assessmentId
      const matchingUAs = values(userAssessments).filter(uA => {
        return uA.assessmentSetId === assessment.id && uA.year === `sy${schoolYear}`
      })

      // get the student's info per collection, and if it isn't there, exit iteration
      const recordStudent = values(studentRecords).filter(sR => sR.email === student.email)[0]
      if (!recordStudent) return
      
      // of the matching UAs, find any that belong to the student
      const matchingUAsWithSameStudent = matchingUAs.filter(mUA => mUA.userId === recordStudent.id)

      // if there is a userAssessment on record for this student with same year
      // and assessmentId, flag it
      if (matchingUAsWithSameStudent.length > 0) {
        const warning = {
          warning: `Student ${recordStudent.fullName} already has a ${assessment.displayName} 
                    recorded for the ${schoolYear} school year. Are you sure you want to overwrite?`,
          rows: [student.row],
        }
        studentWarnings.push(warning)
      }
    }
  })
  
  return { studentWarnings, studentErrors }
}


// WEBPACK FOOTER //
// ./src/helpers/assessment/uploadCSV/verifyStudents.js